import React, { useState } from 'react';
import SwipeToDelete from 'react-swipe-to-delete-ios';
import { shallow } from 'zustand/shallow';
import { useMoney } from 'src/hooks/useMoney';
import { MenuItemEntity } from 'src/services/apis/types';
import { usePOSStore } from 'src/states/pos';
import { clickable } from 'src/utils/a11y';

interface OrderLineProps {
  orderLine: {
    orderItem: MenuItemEntity;
    quantity: number;
    subTotal: number;
  };
  index: number;
}

const OrderLine = React.memo((props: OrderLineProps) => {
  const [editingTotal, setEditingTotal] = useState(false);
  const { format, usesThousandsShortcut, decimals, isKnown } = useMoney();
  const { deleteOrderItem, setOrderItemSubTotal } = usePOSStore(
    (state) => ({
      deleteOrderItem: state.deleteOrderItem,
      setOrderItemSubTotal: state.setOrderItemSubTotal,
    }),
    shallow
  );

  // VND still types in thousands ("50" -> 50.000 ₫); other currencies type the real amount
  // (with cents), the same convention MoneyInput uses.
  const factor = usesThousandsShortcut ? 1000 : 1;

  const handleChangeSubtotal = (e, isBlur = false) => {
    if (!isKnown) {
      return;
    }
    const value = e.target.value;
    if (isBlur || e.key === 'Enter' || e.keyCode === 13) {
      setEditingTotal(false);
      const typed = parseFloat(value);
      if (Number.isNaN(typed)) {
        return;
      }
      const scale = 10 ** decimals;
      return setOrderItemSubTotal(props.orderLine.orderItem, Math.round(typed * factor * scale) / scale);
    }
  };

  return (
    <SwipeToDelete
      onDelete={() => deleteOrderItem(props.orderLine.orderItem)}
      height={60}
      rtl={false}
      className={'pos-current-order-item-std'}
    >
      <div className="pos-current-order-item">
        <div className="index">{props.index}</div>
        <h4>{props.orderLine.orderItem.name}</h4>
        <span>x{props.orderLine.quantity}</span>
        {!editingTotal && <h5 {...clickable(() => setEditingTotal(true))}>{format(props.orderLine.subTotal)}</h5>}
        {editingTotal && (
          <input
            type="number"
            min={0}
            step="any"
            disabled={!isKnown}
            defaultValue={props.orderLine.subTotal / factor}
            // Deliberate: this field opens inside a modal the operator just chose to
            // open, so focus is expected rather than stolen on page load.
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onKeyUp={handleChangeSubtotal}
            onBlur={(e) => handleChangeSubtotal(e, true)}
          />
        )}
      </div>
    </SwipeToDelete>
  );
});

export default OrderLine;
